import {
  Button,
  Form,
  Icon,
  Input,
  Modal,
  Tooltip,
  message,
  Card,
  Divider
} from 'antd'
import React, { useState, useRef, useEffect } from 'react'

import './ScenarioForm.scss'
import { CreatableSelect } from '../../../../../../../../../components'

import scenarioFormOptions from './scenarioFormOptions'
import { formatLabel } from '../../utils'
import { api } from '../../../../../../../../../services'
import { useTranslation } from 'react-i18next'

function ScenarioForm({ onCancel, form, onSubmit, extractedText, language }) {
  const { getFieldDecorator, validateFields } = form

  const [isGeneratingScenario, setIsGeneratingScenario] = useState(false)
  const [generationMode, setGenerationMode] = useState('form') // 'form' or 'chat'
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [isChatLoading, setIsChatLoading] = useState(false)
  const [generatedScenario, setGeneratedScenario] = useState(null)
  const [isUpdatingScenario, setIsUpdatingScenario] = useState(false)
  const [hasStartedChat, setHasStartedChat] = useState(false)
  const [showScenarioModal, setShowScenarioModal] = useState(false)
  const [selectedScenario, setSelectedScenario] = useState(null)
  const chatContainerRef = useRef(null)

  const { t } = useTranslation()

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [chatMessages])

  // Handle streaming response data with proper <<END>> parsing
  const handleStreamingResponse = (streamData, messageId) => {
    if (typeof streamData === 'string') {
      // Check if response contains scenario data after <<END>>
      if (streamData.includes('<<END>>')) {
        const [chatText, scenarioText] = streamData.split('<<END>>')

        // Stream the chat text
        let currentIndex = 0
        const finalChatText = chatText.trim()

        const updateMessage = () => {
          if (currentIndex <= finalChatText.length) {
            const currentText = finalChatText.substring(0, currentIndex)
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === messageId
                  ? {
                    ...msg,
                    content: currentText,
                    isStreaming: currentIndex < finalChatText.length,
                    hasScenario: false
                  }
                  : msg
              )
            )
            currentIndex += Math.max(1, Math.floor(finalChatText.length / 50))

            if (currentIndex < finalChatText.length) {
              setTimeout(updateMessage, 30)
            } else {
              // Finished streaming chat text, now parse scenario data
              parseScenarioData(scenarioText, messageId, finalChatText)
            }
          }
        }

        updateMessage()
      } else {
        // No scenario data, just stream the text normally
        streamTextResponse(streamData, messageId)
      }
    } else if (typeof streamData === 'object') {
      // Handle direct scenario object
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? {
              ...msg,
              content: t('v5:generate_scenario'),
              isStreaming: false,
              hasScenario: true,
              scenarioData: streamData
            }
            : msg
        )
      )
      setGeneratedScenario(streamData)
    }
  }

  const streamTextResponse = (text, messageId) => {
    let currentIndex = 0

    const updateMessage = () => {
      if (currentIndex <= text.length) {
        const currentText = text.substring(0, currentIndex)
        setChatMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? {
                ...msg,
                content: currentText,
                isStreaming: currentIndex < text.length,
                language
              }
              : msg
          )
        )
        currentIndex += Math.max(1, Math.floor(text.length / 50))

        if (currentIndex < text.length) {
          setTimeout(updateMessage, 30)
        }
      }
    }

    updateMessage()
  }

  const parseScenarioData = (scenarioText, messageId, chatText) => {
    try {
      if (!scenarioText) return false

      let cleanedText = scenarioText
        .trim()
        .replace(/^\n+/, '') // Remove leading newlines
        .replace(/\n+$/, '') // Remove trailing newlines
        .replace(/\\"/g, '"') // Fix escaped quotes

      // Convert single quotes to double quotes for JSON
      cleanedText = cleanedText.replace(/'/g, '"')

      // Fix apostrophes and contractions with regex patterns
      // This covers all contractions and possessives without hardcoding specific words
      cleanedText = cleanedText
        // Fix contractions: word + "t, "s, "re, "ve, "ll, "d
        .replace(/(\w+)"(t|s|re|ve|ll|d)\b/g, "$1'$2")
        // Fix possessives: word + "s (but not at end of JSON values)
        .replace(/(\w+)"s(?=[\s,\]\}])/g, "$1's")
        // Fix "I"m, "I"ve, "I"ll patterns
        .replace(/\b"([Ii])"([mv]|ll)\b/g, "'$1'$2")
        // Fix any remaining "word" patterns that should be 'word'
        .replace(/(?<=[a-zA-Z])"(?=[a-zA-Z])/g, "'")

      console.log('Regex cleaned text:', cleanedText)

      const parsedData = JSON.parse(cleanedText)
      const parsedScenario = Array.isArray(parsedData)
        ? parsedData[0]
        : parsedData

      if (
        parsedScenario &&
        (parsedScenario.scenario || parsedScenario.options)
      ) {
        setGeneratedScenario(parsedScenario)
        setChatMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? {
                ...msg,
                content: chatText,
                isStreaming: false,
                hasScenario: true,
                scenarioData: parsedScenario,
                language
              }
              : msg
          )
        )
        return true
      }
    } catch (error) {
      console.error('Regex parsing failed:', error)
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? {
              ...msg,
              content: chatText,
              isStreaming: false,
              hasScenario: false,
              language
            }
            : msg
        )
      )
    }

    return false
  }

  // Initialize chat when switching to chat mode
  const initializeChat = async () => {
    if (hasStartedChat) return

    setIsChatLoading(true)
    setHasStartedChat(true)
    const messageId = Date.now()

    // Add placeholder message that will be updated with streaming content
    setChatMessages([
      {
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        id: messageId,
        isStreaming: true,
        language
      }
    ])

    try {
      const data = {
        type: 'chat',
        course_content: extractedText,
        message: 'start'
      }

      const response = await api.courses.scenarioChat(data)

      // Handle streaming response
      if (response.data) {
        handleStreamingResponse(response.data, messageId)
      } else {
        // Fallback
        setChatMessages([
          {
            type: 'assistant',
            content: t('v5:will_help_you_create_scenario'),
            timestamp: new Date(),
            id: messageId,
            isStreaming: false,
            language
          }
        ])
      }
    } catch (error) {
      console.error('Error initializing chat:', error)
      message.error('Failed to initialize chat')
      setChatMessages([
        {
          type: 'assistant',
          content: t('v5:will_help_you_create_scenario'),
          timestamp: new Date(),
          id: messageId,
          isStreaming: false,
          language
        }
      ])
    } finally {
      setIsChatLoading(false)
    }
  }

  const handleChatSubmit = async e => {
    e && e.preventDefault()
    if (!chatInput.trim() || isChatLoading) return

    const userMessage = {
      type: 'user',
      content: chatInput.trim(),
      timestamp: new Date(),
      id: Date.now(),
      language
    }
    const messageId = Date.now() + 1

    // Add user message and placeholder for assistant response
    setChatMessages(prev => [
      ...prev,
      userMessage,
      {
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        id: messageId,
        isStreaming: true,
        language
      }
    ])

    setChatInput('')
    setIsChatLoading(true)

    try {
      const data = generatedScenario
        ? {
          type: 'update',
          generated_scenario: generatedScenario,
          message: userMessage.content,
          language
        }
        : {
          type: 'chat',
          course_content: extractedText,
          message: userMessage.content,
          language
        }

      const response = await api.courses.scenarioChat(data)

      // Handle streaming response
      if (response.data) {
        handleStreamingResponse(response.data, messageId)
      } else {
        // Fallback
        setChatMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? {
                ...msg,
                content: 'I received your message.',
                isStreaming: false,
                language
              }
              : msg
          )
        )
      }
    } catch (error) {
      console.error('Error sending chat message:', error)
      message.error('Failed to send message')
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? {
              ...msg,
              content: t('v5:sorry_encountered_an_error'),
              isStreaming: false,
              language
            }
            : msg
        )
      )
    } finally {
      setIsChatLoading(false)
    }
  }

  const handleFormSubmit = e => {
    e.preventDefault()

    validateFields((err, values) => {
      if (!err) {
        setIsGeneratingScenario(true)
        console.log('Received values of form: ', values)
        const pageContent = extractedText

        generateScenario(pageContent, values)
      }
    })
  }

  const generateScenario = (pageContent, scenarioElements) => {
    const data = {
      type: 'normal',
      course_content: pageContent,
      scenario_elements: scenarioElements,
      language
    }

    api.courses
      .generateScenario(data)
      .then(res => {
        const resData = res.data.body && res.data.body[0]
        onSubmit(resData)
      })
      .catch(err => {
        console.log(err)
        message.error('Failed to generate scenario')
      })
      .finally(() => {
        setIsGeneratingScenario(false)
      })
  }

  const handleViewScenario = scenario => {
    setSelectedScenario(scenario)
    setShowScenarioModal(true)
  }

  const handleScenarioModalClose = () => {
    setShowScenarioModal(false)
    setSelectedScenario(null)
  }

  const handleUseScenario = () => {
    if (selectedScenario) {
      onSubmit(selectedScenario)
    } else if (generatedScenario) {
      onSubmit(generatedScenario)
    }
    handleScenarioModalClose()
  }

  const renderChat = () => (
    <div className="chat-container">
      {!hasStartedChat ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Card>
            <Icon
              type="message"
              style={{
                fontSize: '48px',
                color: '#1890ff',
                marginBottom: '16px'
              }}
            />
            <h3>{t('v5:chat_based_scenario_generation')}</h3>
            <p>{t('v5:start_conversation_to_generate')}</p>
            <Button
              type="primary"
              size="large"
              onClick={initializeChat}
              loading={isChatLoading}
            >
              {t('v5:start_chat')}
            </Button>
          </Card>
        </div>
      ) : (
        <>
          <div
            className="chat-messages"
            ref={chatContainerRef}
            style={{
              height: '350px',
              overflowY: 'auto',
              marginBottom: '16px',
              padding: '12px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              backgroundColor: '#fafafa'
            }}
          >
            {chatMessages.map(msg => (
              <div
                key={msg.id}
                className={`chat-message-item ${msg.type}`}
                style={{ marginBottom: '16px' }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    backgroundColor: msg.type === 'user' ? '#1890ff' : '#fff',
                    color: msg.type === 'user' ? 'white' : 'black',
                    marginLeft: msg.type === 'user' ? '20%' : '0',
                    marginRight: msg.type === 'user' ? '0' : '20%',
                    position: 'relative',
                    border:
                      msg.type === 'assistant' ? '1px solid #e8e8e8' : 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                    {msg.content}
                  </div>
                  {msg.isStreaming && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: '2px',
                        height: '16px',
                        backgroundColor:
                          msg.type === 'user'
                            ? 'rgba(255,255,255,0.8)'
                            : '#1890ff',
                        marginLeft: '4px',
                        animation: 'blink 1s infinite'
                      }}
                    ></span>
                  )}
                  {msg.hasScenario && (
                    <div style={{ marginTop: '12px' }}>
                      <Button
                        type="primary"
                        size="small"
                        icon="eye"
                        onClick={() =>
                          handleViewScenario(
                            msg.scenarioData || generatedScenario
                          )
                        }
                      >
                        {t('v5:view_scenario')}
                      </Button>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#999',
                    marginTop: '4px',
                    textAlign: msg.type === 'user' ? 'right' : 'left'
                  }}
                >
                  {msg.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>

          <Form
            onSubmit={handleChatSubmit}
            style={{ display: 'flex', gap: '8px' }}
          >
            <Input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              placeholder="Type your message..."
              onPressEnter={handleChatSubmit}
              disabled={isChatLoading}
              style={{ borderRadius: '20px' }}
            />
            <Button
              type="primary"
              htmlType="submit"
              loading={isChatLoading}
              disabled={!chatInput.trim()}
              shape="round"
            >
              {t('v3:send')}
            </Button>
          </Form>
        </>
      )}
    </div>
  )

  const renderForm = () => (
    <Form className="scenario-form" onSubmit={handleFormSubmit}>
      {Object.entries(scenarioFormOptions).map(([key, values]) => (
        <Form.Item
          labelAlign="top"
          label={
            <span className="scenario-form-label">
              <span>{formatLabel(key)} </span>
              <Tooltip title={t(values.description)}>
                <Icon type="info-circle" />
              </Tooltip>
            </span>
          }
          key={key}
        >
          {getFieldDecorator(key, {
            initialValue: Array.isArray(values.value) ? [] : values.value,
            rules: [
              {
                required: true,
                message: `${t('v4:please_input_your')} ${formatLabel(key)}!`
              }
            ]
          })(
            values.value &&
              Array.isArray(values.value) &&
              values.value.length > 0 ? (
              <CreatableSelect
                fieldKey={key}
                updateOptionValue={(value, field) =>
                  form.setFieldsValue({ [field]: value })
                }
                options={values.value}
                placeholder={`${t('v4:select_or_type_your')} ${formatLabel(
                  key
                )}`}
              />
            ) : (
              <Input.TextArea
                placeholder={`${t('v4:enter_your')} ${formatLabel(key)}`}
              />
            )
          )}
        </Form.Item>
      ))}
      <div className="form-button-center">
        <Form.Item>
          <Button
            className="shl-primary-btn"
            htmlType="submit"
            shape="round"
            size="large"
            loading={isGeneratingScenario}
          >
            {t('v4:generate_world_summary')}
          </Button>
        </Form.Item>
      </div>
    </Form>
  )

  const renderScenarioModal = () => {
    if (!selectedScenario) return null

    const correctAnswerIndex = parseInt(selectedScenario.correct_answer) - 1

    return (
      <Modal
        title="Generated Scenario Details"
        visible={showScenarioModal}
        onCancel={handleScenarioModalClose}
        footer={[
          <Button key="cancel" onClick={handleScenarioModalClose}>
            {t('v3:close')}
          </Button>,
          <Button key="use" type="primary" onClick={handleUseScenario}>
            {t('v5:use_this_scenario')}
          </Button>
        ]}
        width={800}
      >
        <div className="scenario-details">
          <div className="scenario-section" style={{ marginBottom: '24px' }}>
            <h4 style={{ color: '#1890ff', marginBottom: '12px' }}>
              {t('v4:scenario_escription')}
            </h4>
            <div
              style={{
                backgroundColor: '#f8f9fa',
                padding: '16px',
                borderRadius: '8px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}
            >
              {selectedScenario.scenario}
            </div>
          </div>

          <div className="scenario-section" style={{ marginBottom: '24px' }}>
            <h4 style={{ color: '#1890ff', marginBottom: '12px' }}>Options:</h4>
            <div>
              {selectedScenario.options &&
                selectedScenario.options.map((option, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor:
                        index === correctAnswerIndex ? '#f6ffed' : '#fafafa',
                      border: `1px solid ${index === correctAnswerIndex ? '#b7eb8f' : '#e8e8e8'
                        }`,
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        minWidth: '20px'
                      }}
                    >
                      {String.fromCharCode(65 + index)})
                    </span>
                    <span style={{ flex: 1 }}>{option}</span>
                    {index === correctAnswerIndex && (
                      <Icon type="check-circle" style={{ color: '#52c41a' }} />
                    )}
                  </div>
                ))}
            </div>
          </div>

          <div className="scenario-section">
            <h4 style={{ color: '#1890ff', marginBottom: '12px' }}>
              {t('v4:feedback')}:
            </h4>
            <div
              style={{
                backgroundColor: '#e6f7ff',
                padding: '16px',
                borderRadius: '8px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}
            >
              {selectedScenario.feedback}
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  // Reset chat when switching modes
  useEffect(() => {
    if (generationMode === 'chat') {
      if (!hasStartedChat) {
        setChatMessages([])
        setGeneratedScenario(null)
      }
    } else {
      // Keep the generated scenario when switching to form mode
    }
  }, [generationMode])

  return (
    <>
      <Modal
        title={t('v5:scenario_generation')}
        visible
        footer={null}
        centered
        closable
        onCancel={onCancel}
        className="scenario-generation"
        width={900}
      >
        <style>
          {`
            @keyframes blink {
              0%, 50% { opacity: 1; }
              51%, 100% { opacity: 0; }
            }
            .chat-message.user {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
            }
            .chat-message.assistant {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            }
          `}
        </style>

        <div style={{ marginBottom: '16px' }}>
          <Button.Group>
            <Button
              type={generationMode === 'form' ? 'primary' : 'default'}
              onClick={() => setGenerationMode('form')}
            >
              <Icon type="form" /> {t('v4:form_mode')}
            </Button>
            <Button
              type={generationMode === 'chat' ? 'primary' : 'default'}
              onClick={() => setGenerationMode('chat')}
            >
              <Icon type="message" /> {t('v4:chat_mode')}
            </Button>
          </Button.Group>
        </div>

        <Divider />

        {generationMode === 'form' ? renderForm() : renderChat()}
      </Modal>

      {/* Scenario Details Modal */}
      {renderScenarioModal()}
    </>
  )
}

export default Form.create()(ScenarioForm)
